import { useFormikContext } from "formik";
import { useMemo } from "react";

import { FieldLabel, FieldSelect, FormRowField } from "@skilitics/designer-ui-components";
import { useFormatMessage } from "@skilitics/localization";
import { isPrimitiveValue } from "@skilitics/nautilus-core-designer-data";

import { useTriggerScopeNodeId } from "../../../triggers/HandlerDesignerContext.ts";
import { useOperatorOptions } from "../../forms/comparison-views/useOperatorOptions.ts";
import { ValueConfigEditor } from "../../forms/ValueConfigEditor.tsx";
import type { ComparisonFieldValues, ComparisonViewProps } from "../../valueSubtypeDefinition.ts";
import type { StringValueSubtypeSpec, ValueLiteralOption } from "../../valueSubtypeSpecs.ts";
import { localeOperators } from "./localeOperators.ts";
import type { LocaleSubtype } from "./localeSubtype.ts";
import { requireLocale } from "./localeSubtype.ts";

/** Stable, so a spec-less render does not re-memo the offer. */
const noLocaleOptions: readonly ValueLiteralOption[] = [];

/**
 * The operator and operand rows of a locale comparison: the language is picked
 * from the module's content languages by title rather than typed as a code, and
 * only other locales are offered to bind. Rows only — a Form mounts it.
 *
 * The languages arrive in the spec, derived from state rather than carried by
 * the subtype instance — which is what a subtype can do and an enum cannot: an
 * enum carries its values, so it could only ever show the codes.
 */
export const LocaleComparisonView = ({
  subtypeSpec,
  scopeNodeId,
  excludeNodeId,
  bindable,
  disabled = false,
}: ComparisonViewProps<LocaleSubtype, StringValueSubtypeSpec>) => {
  const formatMessage = useFormatMessage(localeComparisonFormatStrings);
  const triggerScopeNodeId = useTriggerScopeNodeId();
  const { values } = useFormikContext<ComparisonFieldValues>();
  const operatorOptions = useOperatorOptions(localeOperators, values.operator);

  const options = subtypeSpec?.options ?? noLocaleOptions;
  const storedValue = isPrimitiveValue(values.value) ? String(values.value) : undefined;
  // A stored code no longer among the content languages is offered alongside, as
  // the operator list does: an author can only change what they can see, and a
  // removed language strands the comparison rather than deleting it.
  const valueOptions = useMemo(
    () =>
      storedValue !== undefined && !options.some(({ value }) => value === storedValue)
        ? [...options, { value: storedValue, label: storedValue }]
        : options,
    [options, storedValue],
  );
  const requirement = useMemo(() => requireLocale(), []);

  return (
    <>
      <FormRowField<ComparisonFieldValues> name="operator">
        <FieldLabel />
        <FieldSelect disabled={disabled} options={operatorOptions} placeholder={formatMessage("operatorPlaceholder")} />
      </FormRowField>

      <FormRowField<ComparisonFieldValues> name="value">
        <FieldLabel />
        <ValueConfigEditor
          type="string"
          subtype={requirement}
          scopeNodeId={scopeNodeId ?? triggerScopeNodeId}
          excludeNodeId={excludeNodeId}
          bindable={bindable}
          disabled={disabled}
        >
          <FieldSelect disabled={disabled} options={valueOptions} placeholder={formatMessage("valuePlaceholder")} />
        </ValueConfigEditor>
      </FormRowField>
    </>
  );
};

/** Spread into the mounting form's `formatStrings` — an implicit `FieldLabel` reads its text from there. */
export const localeComparisonFormatStrings = {
  operatorLabel: {
    en: "Operator",
  },
  operatorPlaceholder: {
    en: "Select an operator",
  },
  valueLabel: {
    en: "Value",
  },
  valuePlaceholder: {
    en: "Select a language",
  },
};
